/**
 * ProgramWizard.jsx — Questionnaire guidé "gros boutons" réutilisé pour (1) la génération IA
 * self-service, (2) la demande de programme à un coach certifié, et (3) la création manuelle
 * assistée (débutant). mode='ai' (cyan), mode='coach' (magenta), mode='assisted' (émeraude).
 * Rend un objet structuré via onComplete(data), le parent décide quoi en faire.
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dumbbell, Flame, Zap, Timer, HeartPulse, Trophy, Building2, Home, TreeDeciduous, Shuffle,
  Weight, Waves, Layers, PersonStanding, Bike, Sofa, Cable, HelpCircle, ChevronLeft, ChevronRight,
  Check, Bot, MessageSquareHeart, ClipboardList, Wand2,
} from 'lucide-react';

const THEMES = {
  ai: { bg: 'bg-cyan-500', border: 'border-cyan-400/60', shadow: 'shadow-[0_0_24px_rgba(34,211,238,0.3)]', text: 'text-cyan-300', textStrong: 'text-cyan-400', ring: 'focus:ring-cyan-500/40', grad: 'from-cyan-400 to-blue-500', activeBg: 'bg-cyan-500/20 border-cyan-500/50 text-cyan-200', hoverShadow: 'hover:shadow-cyan-500/25' },
  coach: { bg: 'bg-fuchsia-600', border: 'border-fuchsia-500/60', shadow: 'shadow-[0_0_24px_rgba(217,70,239,0.3)]', text: 'text-fuchsia-300', textStrong: 'text-fuchsia-400', ring: 'focus:ring-fuchsia-500/40', grad: 'from-fuchsia-500 to-pink-500', activeBg: 'bg-fuchsia-500/20 border-fuchsia-500/50 text-fuchsia-200', hoverShadow: 'hover:shadow-fuchsia-500/25' },
  assisted: { bg: 'bg-emerald-500', border: 'border-emerald-400/60', shadow: 'shadow-[0_0_24px_rgba(16,185,129,0.3)]', text: 'text-emerald-300', textStrong: 'text-emerald-400', ring: 'focus:ring-emerald-500/40', grad: 'from-emerald-400 to-teal-500', activeBg: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200', hoverShadow: 'hover:shadow-emerald-500/25' },
};

const OBJECTIVES = [
  { value: 'Prise de muscle', icon: Dumbbell },
  { value: 'Perte de poids', icon: Flame },
  { value: 'Augmentation de la force', icon: Zap },
  { value: 'Endurance musculaire', icon: Timer },
  { value: 'Remise en forme générale', icon: HeartPulse },
  { value: 'Préparation sportive / mobilité', icon: Trophy },
];

const LOCATIONS = [
  { value: 'Salle de fitness', icon: Building2 },
  { value: 'À la maison', icon: Home },
  { value: 'Extérieur', icon: TreeDeciduous },
  { value: 'Hybride (salle + maison)', icon: Shuffle },
];

const EQUIPMENT_OPTIONS = [
  { id: 'halteres', label: 'Haltères', backendTag: 'Haltère', icon: Dumbbell, placeholder: 'Ex: 2 haltères de 5kg, 1 seul de 8kg...' },
  { id: 'kettlebell', label: 'Kettlebell', backendTag: 'Kettlebell', icon: Weight, placeholder: 'Ex: 1 seul kettlebell de 8kg...' },
  { id: 'elastiques', label: 'Élastiques', backendTag: 'Accessoire', icon: Waves, placeholder: 'Ex: 2 bandes de résistance moyenne...' },
  { id: 'barre', label: 'Barre + poids', backendTag: 'Barre', icon: Layers, placeholder: "Ex: barre 15kg + disques jusqu'à 40kg..." },
  { id: 'banc', label: 'Banc', backendTag: 'Accessoire', icon: Sofa, placeholder: 'Ex: banc plat uniquement...' },
  { id: 'trx', label: 'TRX / sangles', backendTag: 'Accessoire', icon: Cable, placeholder: 'Ex: 1 sangle TRX fixée à la porte...' },
  { id: 'cardio', label: 'Tapis / vélo', backendTag: 'Accessoire', icon: Bike, placeholder: 'Ex: tapis de course, pas de vélo...' },
  { id: 'bodyweight', label: 'Poids du corps uniquement', backendTag: null, icon: PersonStanding, placeholder: '' },
  { id: 'autre', label: 'Autre', backendTag: null, icon: HelpCircle, placeholder: 'Précisez le matériel dont vous disposez...' },
];

const LEVELS = ['Débutant', 'Intermédiaire', 'Avancé'];

export const INJURY_ZONES = [
  { id: 'epaule', label: 'Épaule', groups: ['Épaules'] },
  { id: 'dos', label: 'Dos / lombaires', groups: ['Dos', 'Lombaires', 'Trapèzes'] },
  { id: 'genou', label: 'Genou', groups: ['Quadriceps', 'Ischio-jambiers', 'Fessiers'] },
  { id: 'poignet_coude', label: 'Poignet / coude', groups: ['Biceps', 'Triceps', 'Avant-bras'] },
  { id: 'hanche', label: 'Hanche', groups: ['Fessiers', 'Adducteurs / Abducteurs'] },
  { id: 'cheville_mollet', label: 'Cheville / mollet', groups: ['Mollets'] },
];

const ACCENT_MAP = { à: 'a', â: 'a', é: 'e', è: 'e', ê: 'e', ë: 'e', î: 'i', ï: 'i', ô: 'o', û: 'u', ù: 'u', ü: 'u', ç: 'c' };
const slugify = (s) => s.toLowerCase().replace(/[àâéèêëîïôûùüç]/g, (c) => ACCENT_MAP[c] || c).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const blankData = (initial = {}) => ({
  goal: initial.goal || '', location: initial.location || '',
  equipmentIds: [], equipmentDetails: {},
  daysPerWeek: initial.daysPerWeek || 4, level: initial.level || '',
  durationWeeks: initial.durationWeeks || 8, sessionDurationMinutes: initial.sessionDurationMinutes || '',
  injuryIds: [], extraNotes: '',
});

const SelectableCard = ({ selected, onClick, icon: Icon, label, testId, T }) => (
  <button
    type="button" onClick={onClick} data-testid={testId}
    aria-pressed={selected}
    className={`relative overflow-hidden flex flex-col items-center justify-center gap-2.5 p-5 rounded-2xl text-center transition-all duration-300 active:scale-95 border ${
      selected ? `bg-white/10 backdrop-blur-xl ${T.border} ${T.shadow}` : 'bg-white/[0.04] backdrop-blur-xl border-white/10 hover:bg-white/[0.08] hover:border-white/25'
    }`}
  >
    <Icon className={`w-7 h-7 transition-transform ${selected ? `scale-110 ${T.text}` : 'text-zinc-400'}`} />
    <span className={`text-sm font-bold leading-tight ${selected ? 'text-white' : 'text-zinc-300'}`}>{label}</span>
    {selected && <Check className={`w-4 h-4 absolute top-2.5 right-2.5 ${T.textStrong}`} />}
  </button>
);

const StepShell = ({ children, direction = 1 }) => (
  <AnimatePresence mode="wait">
    <motion.div
      key={typeof children === 'object' ? undefined : children}
      initial={{ x: 24 * direction, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -24 * direction, opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      {children}
    </motion.div>
  </AnimatePresence>
);

export const ProgramWizard = ({ en, mode = 'ai', initial, onCancel, onComplete, submitLabel, submitIcon: SubmitIcon }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const T = THEMES[mode] || THEMES.ai;
  const [data, setData] = useState(() => blankData(initial));
  const [stepIdx, setStepIdx] = useState(0);

  const needsEquipment = data.location && data.location !== 'Salle de fitness';
  const steps = needsEquipment ? ['goal', 'location', 'equipment', 'details', 'injuries', 'summary'] : ['goal', 'location', 'details', 'injuries', 'summary'];
  const step = steps[stepIdx];
  const pct = Math.round(((stepIdx + 1) / steps.length) * 100);

  const goNext = () => setStepIdx((i) => Math.min(steps.length - 1, i + 1));
  const goBack = () => { if (stepIdx === 0) { onCancel && onCancel(); return; } setStepIdx((i) => Math.max(0, i - 1)); };

  const toggleEquipment = (id) => {
    setData((d) => {
      if (id === 'bodyweight') {
        return { ...d, equipmentIds: d.equipmentIds.includes('bodyweight') ? [] : ['bodyweight'] };
      }
      const withoutBw = d.equipmentIds.filter((x) => x !== 'bodyweight');
      const next = withoutBw.includes(id) ? withoutBw.filter((x) => x !== id) : [...withoutBw, id];
      return { ...d, equipmentIds: next };
    });
  };
  const setDetail = (id, text) => setData((d) => ({ ...d, equipmentDetails: { ...d.equipmentDetails, [id]: text } }));
  const toggleInjury = (id) => setData((d) => ({ ...d, injuryIds: d.injuryIds.includes(id) ? d.injuryIds.filter((x) => x !== id) : [...d.injuryIds, id] }));

  const buildPayload = () => {
    const selected = EQUIPMENT_OPTIONS.filter((o) => data.equipmentIds.includes(o.id));
    const tags = [...new Set(selected.map((o) => o.backendTag).filter(Boolean))];
    let equipmentText = selected
      .map((o) => `${o.label}${data.equipmentDetails[o.id] ? ` : ${data.equipmentDetails[o.id]}` : ''}`)
      .join(' ; ');
    if (data.equipmentIds.includes('bodyweight') && !equipmentText) equipmentText = 'Aucun matériel, poids du corps uniquement.';
    const injuryZones = INJURY_ZONES.filter((z) => data.injuryIds.includes(z.id));
    const injuryGroups = [...new Set(injuryZones.flatMap((z) => z.groups))];
    const injuryNote = injuryZones.length ? `Blessure(s) signalée(s) : ${injuryZones.map((z) => z.label).join(', ')} — groupes musculaires concernés évités automatiquement.` : null;
    return {
      goal: data.goal, location: data.location,
      equipmentTags: tags.length ? tags : null, equipment: equipmentText || null,
      daysPerWeek: data.daysPerWeek, level: data.level || null,
      durationWeeks: data.durationWeeks, sessionDurationMinutes: data.sessionDurationMinutes || null,
      injuryZones: injuryZones.map((z) => z.id), injuryGroups,
      extraNotes: [injuryNote, data.extraNotes || null].filter(Boolean).join(' ') || null,
    };
  };

  const canEquipment = !needsEquipment || data.equipmentIds.length > 0;
  const defaultSubmitLabel = mode === 'coach' ? L('Envoyer à mon coach', 'Send to my coach') : mode === 'assisted' ? L('Voir mon programme suggéré', 'See my suggested program') : L("Générer avec l'IA", 'Generate with AI');
  const DefaultSubmitIcon = mode === 'coach' ? MessageSquareHeart : mode === 'assisted' ? Wand2 : Bot;
  const FinalIcon = SubmitIcon || DefaultSubmitIcon;

  return (
    <div className="space-y-5" data-testid="program-wizard">
      <div className={`absolute -z-10 w-72 h-72 rounded-full blur-3xl opacity-20 ${T.bg}`} aria-hidden="true" />
      <div className="flex items-center justify-between gap-3">
        <button onClick={goBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="wizard-back-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
        </button>
        <span className="text-xs font-mono text-zinc-500" data-testid="wizard-step-counter">{stepIdx + 1}/{steps.length}</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden" data-testid="wizard-progress-bar">
        <motion.div className={`h-full rounded-full bg-gradient-to-r ${T.grad}`} animate={{ width: `${pct}%` }} transition={{ duration: 0.3 }} />
      </div>

      <StepShell>
        {step === 'goal' && (
          <div className="space-y-4" data-testid="wizard-step-goal">
            <h3 className="text-xl font-black text-white">{L('Quel est votre objectif ?', "What's your goal?")}</h3>
            <div className="grid grid-cols-2 gap-3">
              {OBJECTIVES.map(({ value, icon }) => (
                <SelectableCard key={value} icon={icon} label={value} T={T}
                  selected={data.goal === value} testId={`wizard-goal-${slugify(value)}`}
                  onClick={() => { setData((d) => ({ ...d, goal: value })); goNext(); }} />
              ))}
            </div>
          </div>
        )}

        {step === 'location' && (
          <div className="space-y-4" data-testid="wizard-step-location">
            <h3 className="text-xl font-black text-white">{L('Où allez-vous vous entraîner ?', 'Where will you train?')}</h3>
            <div className="grid grid-cols-2 gap-3">
              {LOCATIONS.map(({ value, icon }) => (
                <SelectableCard key={value} icon={icon} label={value} T={T}
                  selected={data.location === value} testId={`wizard-location-${slugify(value)}`}
                  onClick={() => { setData((d) => ({ ...d, location: value })); goNext(); }} />
              ))}
            </div>
          </div>
        )}

        {step === 'equipment' && (
          <div className="space-y-4" data-testid="wizard-step-equipment">
            <h3 className="text-xl font-black text-white">{L('Quel matériel avez-vous à disposition ?', 'What equipment do you have?')}</h3>
            <p className="text-xs text-zinc-500">{L('Plusieurs choix possibles. Précisez la quantité/poids exact pour un programme au plus juste.', 'Multiple choices allowed. Specify exact quantity/weight for the most accurate program.')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {EQUIPMENT_OPTIONS.map(({ id, label, icon }) => (
                <SelectableCard key={id} icon={icon} label={label} T={T}
                  selected={data.equipmentIds.includes(id)} testId={`wizard-equipment-${id}`}
                  onClick={() => toggleEquipment(id)} />
              ))}
            </div>
            <AnimatePresence>
              {data.equipmentIds.filter((id) => id !== 'bodyweight').map((id) => {
                const opt = EQUIPMENT_OPTIONS.find((o) => o.id === id);
                if (!opt) return null;
                return (
                  <motion.div key={id} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                    <label className="text-[11px] text-zinc-500 mb-1 block">{opt.label} — {L('précisez', 'specify')}</label>
                    <input value={data.equipmentDetails[id] || ''} onChange={(e) => setDetail(id, e.target.value)}
                      placeholder={opt.placeholder} data-testid={`wizard-equipment-detail-${id}`}
                      className={`w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 ${T.ring}`} />
                  </motion.div>
                );
              })}
            </AnimatePresence>
            <button onClick={goNext} disabled={!canEquipment} data-testid="wizard-equipment-next-btn"
              className={`w-full py-3.5 rounded-full font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition-transform active:scale-95 bg-gradient-to-r ${T.grad}`}>
              {L('Continuer', 'Continue')} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === 'details' && (
          <div className="space-y-4" data-testid="wizard-step-details">
            <h3 className="text-xl font-black text-white">{L('Quelques détails pratiques', 'A few practical details')}</h3>
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">{L('Séances par semaine', 'Sessions per week')}</label>
              <div className="flex items-center gap-2" data-testid="wizard-days-stepper">
                <button onClick={() => setData((d) => ({ ...d, daysPerWeek: Math.max(1, d.daysPerWeek - 1) }))} className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/10 text-white font-bold">−</button>
                <span className="flex-1 text-center text-2xl font-black text-white">{data.daysPerWeek}</span>
                <button onClick={() => setData((d) => ({ ...d, daysPerWeek: Math.min(7, d.daysPerWeek + 1) }))} className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/10 text-white font-bold">+</button>
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">{L('Niveau', 'Level')}</label>
              <div className="grid grid-cols-3 gap-2">
                {LEVELS.map((l) => (
                  <button key={l} onClick={() => setData((d) => ({ ...d, level: l }))} data-testid={`wizard-level-${l.toLowerCase()}`}
                    className={`py-2.5 rounded-xl text-xs font-bold border transition-colors ${data.level === l ? T.activeBg : 'bg-white/[0.04] border-white/10 text-zinc-400'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">{L('Durée programme (sem.)', 'Program duration (weeks)')}</label>
                <input type="number" min={1} max={52} value={data.durationWeeks} onChange={(e) => setData((d) => ({ ...d, durationWeeks: +e.target.value }))}
                  data-testid="wizard-duration-weeks-input" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">{L('Durée séance (min)', 'Session duration (min)')}</label>
                <input type="number" min={15} max={180} value={data.sessionDurationMinutes} onChange={(e) => setData((d) => ({ ...d, sessionDurationMinutes: e.target.value }))}
                  placeholder="60" data-testid="wizard-session-duration-input" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-zinc-600" />
              </div>
            </div>
            <button onClick={goNext} data-testid="wizard-details-next-btn"
              className={`w-full py-3.5 rounded-full font-bold text-white flex items-center justify-center gap-2 transition-transform active:scale-95 bg-gradient-to-r ${T.grad}`}>
              {L('Continuer', 'Continue')} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === 'injuries' && (
          <div className="space-y-4" data-testid="wizard-step-injuries">
            <h3 className="text-xl font-black text-white">{L('Une douleur ou blessure à prendre en compte ?', 'Any pain or injury to consider?')}</h3>
            <p className="text-xs text-zinc-500">{L('Les exercices ciblant cette zone seront automatiquement évités. Plusieurs choix possibles.', 'Exercises targeting this zone will be automatically avoided. Multiple choices allowed.')}</p>
            <div className="grid grid-cols-2 gap-2.5" data-testid="wizard-injuries-grid">
              {INJURY_ZONES.map(({ id, label }) => (
                <button key={id} type="button" onClick={() => toggleInjury(id)} data-testid={`wizard-injury-${id}`}
                  className={`py-3 rounded-xl text-sm font-bold border transition-colors ${data.injuryIds.includes(id) ? 'bg-red-500/20 border-red-500/50 text-red-200' : 'bg-white/[0.04] border-white/10 text-zinc-300 hover:border-white/25'}`}>
                  {label}
                </button>
              ))}
            </div>
            {data.injuryIds.length > 0 && (
              <button onClick={() => setData((d) => ({ ...d, injuryIds: [] }))} className="text-xs text-zinc-500 hover:text-white underline" data-testid="wizard-injuries-clear-btn">
                {L('Aucune blessure, tout effacer', 'No injury, clear all')}
              </button>
            )}
            <button onClick={goNext} data-testid="wizard-injuries-next-btn"
              className={`w-full py-3.5 rounded-full font-bold text-white flex items-center justify-center gap-2 transition-transform active:scale-95 bg-gradient-to-r ${T.grad}`}>
              {data.injuryIds.length ? L('Continuer', 'Continue') : L('Aucune, continuer', 'None, continue')} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === 'summary' && (
          <div className="space-y-4" data-testid="wizard-step-summary">
            <h3 className="text-xl font-black text-white flex items-center gap-2">
              <ClipboardList className={`w-5 h-5 ${T.textStrong}`} /> {L('Récapitulatif', 'Summary')}
            </h3>
            <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 space-y-2 text-sm" data-testid="wizard-summary-card">
              <p className="text-zinc-400">{L('Objectif', 'Goal')} : <span className="text-white font-semibold">{data.goal}</span></p>
              <p className="text-zinc-400">{L('Lieu', 'Location')} : <span className="text-white font-semibold">{data.location}</span></p>
              {needsEquipment && (
                <p className="text-zinc-400">{L('Matériel', 'Equipment')} : <span className="text-white font-semibold">{data.equipmentIds.map((id) => EQUIPMENT_OPTIONS.find((o) => o.id === id)?.label).join(', ') || '—'}</span></p>
              )}
              <p className="text-zinc-400">{L('Fréquence', 'Frequency')} : <span className="text-white font-semibold">{data.daysPerWeek}x/{L('semaine', 'week')}</span></p>
              {data.level && <p className="text-zinc-400">{L('Niveau', 'Level')} : <span className="text-white font-semibold">{data.level}</span></p>}
              <p className="text-zinc-400">{L('Durée', 'Duration')} : <span className="text-white font-semibold">{data.durationWeeks} {L('semaines', 'weeks')}{data.sessionDurationMinutes ? ` · ${data.sessionDurationMinutes} min/séance` : ''}</span></p>
              {data.injuryIds.length > 0 && (
                <p className="text-zinc-400">{L('Zones évitées', 'Avoided zones')} : <span className="text-red-300 font-semibold">{data.injuryIds.map((id) => INJURY_ZONES.find((z) => z.id === id)?.label).join(', ')}</span></p>
              )}
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">{L('Notes complémentaires (optionnel)', 'Additional notes (optional)')}</label>
              <textarea value={data.extraNotes} onChange={(e) => setData((d) => ({ ...d, extraNotes: e.target.value }))} rows={3}
                placeholder={L("Ex: blessure à l'épaule à éviter, préfère les supersets...", 'Ex: avoid shoulder injury, prefers supersets...')}
                data-testid="wizard-extra-notes-input" className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 resize-none" />
            </div>
            <button onClick={() => onComplete(buildPayload())} data-testid="wizard-submit-btn"
              className={`w-full py-4 rounded-full font-black text-white flex items-center justify-center gap-2 transition-transform active:scale-95 hover:scale-[1.02] bg-gradient-to-r ${T.grad} shadow-lg ${T.hoverShadow}`}>
              <FinalIcon className="w-4.5 h-4.5" /> {submitLabel || defaultSubmitLabel}
            </button>
          </div>
        )}
      </StepShell>

      {(step === 'goal' || step === 'location') && (
        <p className="text-center text-[11px] text-zinc-600">{L('Cliquez sur une carte pour continuer', 'Click a card to continue')}</p>
      )}
    </div>
  );
};

export default ProgramWizard;
